/**
 * @file usePortalTabs.js
 * @description Shared hook for portal tab state management, responsive tab count,
 * scroll navigation, and drag-to-scroll. Used by IndividualHomePage and ViewLivePage.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useWindowListener } from '../components/helpers/useWindowListener';
import Toast from '../components/ui/toasts/Toast';
import { getText } from '../i18n';

function sortPortalPages(portalPages) {
    return [...portalPages].sort((a, b) => {
        if (a.order !== null && b.order !== null) {
            return a.order - b.order;
        }
        if (a.order === null && b.order === null) {
            return new Date(a.createdAt) - new Date(b.createdAt);
        }
        if (a.order === null) {
            return 1;
        }
        return -1;
    });
}

function usePortalTabs({
    fetchPortalPagesFn,
    routeBuilder,
    onTabClick,
    isChildPortalUrl,
    keepCurrentUrl = false,
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const { portalId: pageId } = useParams();

    const [isLoading, setIsLoading] = useState(true);
    const [tabs, setTabs] = useState([]);
    const [visibleTabCount, setVisibleTabCount] = useState(0);
    const [visibleTabIndex, setVisibleTabIndex] = useState(0);
    const [selectedTabId, setSelectedTabId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [portalId, setPortalId] = useState(0);

    const fetchTabs = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await fetchPortalPagesFn();
            const { viewModels } = result;
            const portalPages = viewModels.map(portalPage => ({
                ...portalPage,
                containers: [],
            }));
            setTabs(sortPortalPages(portalPages));
        } catch (error) {
            console.error(error);
            Toast.error(getText('msg_e1c15b962ddf'));
        } finally {
            setIsLoading(false);
        }
    }, [fetchPortalPagesFn]);

    useEffect(() => {
        if (!tabs || tabs.length === 0) {
            return;
        }

        const tabFromUrl = Number(pageId);
        const tabFromState = Number(location.state?.selectedTabId);
        const hasNavigationStack =
            Array.isArray(location.state?.navigationStack) &&
            location.state.navigationStack.length > 0;

        const selectedFromUrl = Number.isNaN(tabFromUrl)
            ? null
            : tabs.find(tab => tab.id === tabFromUrl);
        const selectedFromState = Number.isNaN(tabFromState)
            ? null
            : tabs.find(tab => tab.id === tabFromState);

        if (
            isChildPortalUrl &&
            isChildPortalUrl({
                tabFromUrl,
                selectedFromUrl,
                hasNavigationStack,
            })
        ) {
            return;
        }

        const nextSelectedTab = selectedFromUrl || selectedFromState || tabs[0];
        if (!nextSelectedTab) {
            return;
        }

        if (selectedTabId !== nextSelectedTab.id) {
            setSelectedTabId(nextSelectedTab.id);
        }

        if (!selectedFromUrl && !keepCurrentUrl) {
            navigate(routeBuilder(nextSelectedTab.id), {
                replace: true,
                state: {
                    ...(location.state || {}),
                    selectedTabId: nextSelectedTab.id,
                },
            });
        }
    }, [
        tabs,
        pageId,
        location.state,
        selectedTabId,
        navigate,
        routeBuilder,
        isChildPortalUrl,
        keepCurrentUrl,
    ]);

    useEffect(() => {
        if (tabs.length > 0) {
            adjustVisibleTabCount();
            const activeTab =
                tabs.find(tab => tab.id === selectedTabId) || tabs[0];
            setPortalId(activeTab.id);
        }
    }, [tabs, selectedTabId]);

    useEffect(() => {
        if (!tabs || tabs.length === 0) return;
        if (selectedTabId === null) return;
        const selectedIndex = tabs.findIndex(t => t.id === selectedTabId);
        if (selectedIndex === -1) return;

        setVisibleTabIndex(prevIndex => {
            const count = visibleTabCount > 0 ? visibleTabCount : tabs.length;
            const maxStart = Math.max(0, tabs.length - count);
            const start = Math.min(Math.max(0, prevIndex || 0), maxStart);

            if (selectedIndex < start) {
                return selectedIndex;
            }
            if (selectedIndex >= start + count) {
                return Math.max(0, selectedIndex - count + 1);
            }
            return start;
        });
    }, [selectedTabId, visibleTabCount, tabs]);

    const adjustVisibleTabCount = () => {
        const width = window.innerWidth;
        let count;
        if (width <= 768) {
            count = tabs.length;
        } else if (width < 900) {
            count = 2;
        } else if (width < 1200) {
            count = 3;
        } else if (width < 1400) {
            count = 4;
        } else if (width < 1600) {
            count = 5;
        } else {
            count = 6;
        }

        count = Math.max(1, Math.min(count, tabs.length || 1));
        setVisibleTabCount(count);

        setVisibleTabIndex(prev => {
            const prevIndex = typeof prev === 'number' ? prev : 0;
            const maxStart = Math.max(0, (tabs.length || 0) - count);
            return Math.min(Math.max(0, prevIndex), maxStart);
        });
    };

    useWindowListener('resize', adjustVisibleTabCount);

    const scroll = direction => {
        const tabsLength = tabs.length;
        if (tabsLength <= 0) return;
        if (direction === 'L' && visibleTabIndex > 0) {
            setVisibleTabIndex(prevIndex => prevIndex - 1);
        } else if (
            direction === 'R' &&
            visibleTabIndex < tabsLength - visibleTabCount
        ) {
            setVisibleTabIndex(prevIndex => prevIndex + 1);
        }
    };

    const openTab = tab => {
        navigate(routeBuilder(tab.id), {
            state: {
                ...(location.state || {}),
                selectedTabId: tab.id,
            },
        });
        if (onTabClick) {
            onTabClick(tab);
        }
    };

    const showLeftArrow = visibleTabIndex > 0;
    const showRightArrow = visibleTabIndex < tabs.length - visibleTabCount;

    const handleMouseDown = e => {
        setIsDragging(true);
        setStartX(e.pageX - e.currentTarget.offsetLeft);
        setScrollLeft(e.currentTarget.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = e => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - e.currentTarget.offsetLeft;
        const walk = (x - startX) * 2;
        e.currentTarget.scrollLeft = scrollLeft - walk;
    };

    const visibleTabs = tabs.slice(
        visibleTabIndex,
        visibleTabIndex + visibleTabCount,
    );

    return {
        isLoading,
        setIsLoading,
        tabs,
        selectedTabId,
        setSelectedTabId,
        portalId,
        setPortalId,
        visibleTabs,
        showLeftArrow,
        showRightArrow,
        scroll,
        openTab,
        fetchTabs,
        handleMouseDown,
        handleMouseLeave,
        handleMouseUp,
        handleMouseMove,
        pageId,
        location,
        navigate,
    };
}

export default usePortalTabs;
